import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { slugify } from '../../utils/slugify';

export interface Enterprise {
  id?: string;
  name: string;
  slug?: string;
  type: 'strategic' | 'regional' | 'specialty' | 'internal';
  status: 'active' | 'pending' | 'suspended' | 'cancelled';
  contact: {
    primaryName: string;
    primaryEmail: string;
    primaryPhone?: string;
    billingEmail?: string;
    technicalEmail?: string;
  };
  quotas: {
    maxChannelPartners: number;
    maxOrganizationsTotal: number;
    maxUsersTotal: number;
    maxStorageGB: number;
  };
  features?: {
    whiteLabeling: boolean;
    customBranding: boolean;
    apiAccess: boolean;
    advancedAnalytics: boolean;
    prioritySupport: boolean;
    customIntegrations: boolean;
    multiRegion: boolean;
    sso: boolean;
  };
  channelPartnerCount?: number;
  organizationCount?: number;
  createdAt?: string;
  isDeleted?: boolean;
}

export interface EnterpriseFormData {
  name: string;
  type: Enterprise['type'];
  status: Enterprise['status'];
  slug?: string;
  contact: {
    primaryName: string;
    primaryEmail: string;
    primaryPhone: string;
    billingEmail: string;
    technicalEmail: string;
  };
  quotas: {
    maxChannelPartners: number;
    maxOrganizationsTotal: number;
    maxUsersTotal: number;
    maxStorageGB: number;
  };
  features: {
    whiteLabeling: boolean;
    customBranding: boolean;
    apiAccess: boolean;
    advancedAnalytics: boolean;
    prioritySupport: boolean;
    customIntegrations: boolean;
    multiRegion: boolean;
    sso: boolean;
  };
}

interface EnterpriseModalProps {
  mode: 'create' | 'edit';
  enterprise?: Enterprise | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: EnterpriseFormData) => Promise<void>;
}

const emptyFeatures: EnterpriseFormData['features'] = {
  whiteLabeling: false,
  customBranding: false,
  apiAccess: false,
  advancedAnalytics: false,
  prioritySupport: false,
  customIntegrations: false,
  multiRegion: false,
  sso: false,
};

const emptyForm: EnterpriseFormData = {
  name: '',
  type: 'strategic',
  status: 'active',
  slug: '',
  contact: {
    primaryName: '',
    primaryEmail: '',
    primaryPhone: '',
    billingEmail: '',
    technicalEmail: '',
  },
  quotas: {
    maxChannelPartners: 10,
    maxOrganizationsTotal: 100,
    maxUsersTotal: 1000,
    maxStorageGB: 5000,
  },
  features: { ...emptyFeatures },
};


const featureLabels: Array<{ key: keyof EnterpriseFormData['features']; label: string }> = [
  { key: 'whiteLabeling', label: 'White Labeling' },
  { key: 'customBranding', label: 'Custom Branding' },
  { key: 'apiAccess', label: 'API Access' },
  { key: 'advancedAnalytics', label: 'Advanced Analytics' },
  { key: 'prioritySupport', label: 'Priority Support' },
  { key: 'customIntegrations', label: 'Custom Integrations' },
  { key: 'multiRegion', label: 'Multi-Region' },
  { key: 'sso', label: 'SSO (Single Sign-On)' },
];

export const EnterpriseModal: React.FC<EnterpriseModalProps> = ({
  mode,
  enterprise,
  open,
  onOpenChange,
  onSave,
}) => {
  const [formData, setFormData] = useState<EnterpriseFormData>(emptyForm);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('identity');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setError(null);
      setValidationErrors({});
      setActiveTab('identity');

      if (enterprise) {
        setFormData({
          name: enterprise.name,
          type: enterprise.type,
          status: enterprise.status,
          slug: enterprise.slug || '',
          contact: {
            primaryName: enterprise.contact.primaryName,
            primaryEmail: enterprise.contact.primaryEmail,
            primaryPhone: enterprise.contact.primaryPhone || '',
            billingEmail: enterprise.contact.billingEmail || '',
            technicalEmail: enterprise.contact.technicalEmail || '',
          },
          quotas: {
            maxChannelPartners: enterprise.quotas.maxChannelPartners,
            maxOrganizationsTotal: enterprise.quotas.maxOrganizationsTotal,
            maxUsersTotal: enterprise.quotas.maxUsersTotal,
            maxStorageGB: enterprise.quotas.maxStorageGB,
          },
          features: enterprise.features
            ? { ...emptyFeatures, ...enterprise.features }
            : { ...emptyFeatures },
        });
      } else {
        setFormData({ ...emptyForm, features: { ...emptyFeatures } });
      }
    }
  }, [open, enterprise]);

  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: mode === 'create' ? slugify(name) : prev.slug,
    }));
    if (validationErrors.name) {
      setValidationErrors(prev => ({ ...prev, name: '' }));
    }
  };

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!formData.name || formData.name.trim().length < 3) {
      errors.name = 'Name must be at least 3 characters';
    }
    if (!formData.contact.primaryName || !formData.contact.primaryName.trim()) {
      errors.primaryName = 'Primary contact name is required';
    }
    if (!formData.contact.primaryEmail || !formData.contact.primaryEmail.trim()) {
      errors.primaryEmail = 'Primary contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact.primaryEmail)) {
      errors.primaryEmail = 'Invalid email format';
    }
    if (
      formData.contact.billingEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact.billingEmail)
    ) {
      errors.billingEmail = 'Invalid billing email format';
    }
    if (
      formData.contact.technicalEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact.technicalEmail)
    ) {
      errors.technicalEmail = 'Invalid technical email format';
    }

    setValidationErrors(errors);
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const errors = validate();
    if (Object.keys(errors).length > 0) {
      if (errors.name) {
        setActiveTab('identity');
      } else if (
        errors.primaryName ||
        errors.primaryEmail ||
        errors.billingEmail ||
        errors.technicalEmail
      ) {
        setActiveTab('contact');
      }
      return;
    }

    setIsLoading(true);
    try {
      await onSave(formData);
      onOpenChange(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save enterprise');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 grid grid-rows-[auto_auto_1fr_auto]">
        {/* Header */}
        <div className="border-b px-6 py-4">
          <DialogHeader>
            <DialogTitle>
              {mode === 'create' ? 'Create New Enterprise' : 'Edit Enterprise'}
            </DialogTitle>
          </DialogHeader>
        </div>

        <form onSubmit={handleSubmit} className="contents">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="contents">
            {/* Tab Navigation */}
            <div className="border-b px-6">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="identity">Identity</TabsTrigger>
                <TabsTrigger value="quotas">Quotas &amp; Features</TabsTrigger>
                <TabsTrigger value="contact">Contact</TabsTrigger>
              </TabsList>
            </div>

            {/* Scrollable Content Area */}
            <div className="overflow-y-auto px-6 py-6">

              {/* Identity Tab */}
              <TabsContent value="identity" className="space-y-6 mt-0">
                <div className="space-y-5">
                  <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b border-gray-200">
                    Enterprise Identity
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Enterprise Name *
                    </label>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      placeholder="Acme Healthcare Group"
                    />
                    {validationErrors.name && (
                      <p className="text-xs text-red-600 mt-1">{validationErrors.name}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1.5">
                      Slug preview:{' '}
                      <span className="font-mono bg-gray-100 px-1 rounded">
                        {formData.slug || '—'}
                      </span>
                      {mode === 'edit' && (
                        <span className="ml-2 text-amber-600">(immutable in edit mode)</span>
                      )}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type *
                    </label>
                    <select
                      className="w-full px-3 py-2 border rounded-md"
                      value={formData.type}
                      onChange={(e) =>
                        setFormData({ ...formData, type: e.target.value as Enterprise['type'] })
                      }
                    >
                      <option value="strategic">Strategic</option>
                      <option value="regional">Regional</option>
                      <option value="specialty">Specialty</option>
                      <option value="internal">Internal</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status *
                    </label>
                    <select
                      className="w-full px-3 py-2 border rounded-md"
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          status: e.target.value as Enterprise['status'],
                        })
                      }
                    >
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                      <option value="suspended">Suspended</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
              </TabsContent>

              {/* Quotas & Features Tab */}
              <TabsContent value="quotas" className="space-y-6 mt-0">
                {/* Quotas */}
                <div className="space-y-5">
                  <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b border-gray-200">
                    Quotas
                  </h3>
                  <p className="text-sm text-gray-500">Use -1 for unlimited.</p>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Channel Partners
                      </label>
                      <Input
                        type="number"
                        value={formData.quotas.maxChannelPartners}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            quotas: {
                              ...formData.quotas,
                              maxChannelPartners: parseInt(e.target.value) || 0,
                            },
                          })
                        }
                        min={-1}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Organizations Total
                      </label>
                      <Input
                        type="number"
                        value={formData.quotas.maxOrganizationsTotal}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            quotas: {
                              ...formData.quotas,
                              maxOrganizationsTotal: parseInt(e.target.value) || 0,
                            },
                          })
                        }
                        min={-1}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Users Total
                      </label>
                      <Input
                        type="number"
                        value={formData.quotas.maxUsersTotal}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            quotas: {
                              ...formData.quotas,
                              maxUsersTotal: parseInt(e.target.value) || 0,
                            },
                          })
                        }
                        min={-1}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Storage (GB)
                      </label>
                      <Input
                        type="number"
                        value={formData.quotas.maxStorageGB}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            quotas: {
                              ...formData.quotas,
                              maxStorageGB: parseInt(e.target.value) || 0,
                            },
                          })
                        }
                        min={-1}
                      />
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-5">
                  <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b border-gray-200">
                    Features
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {featureLabels.map(({ key, label }) => (
                      <label key={key} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.features[key]}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              features: { ...formData.features, [key]: e.target.checked },
                            })
                          }
                          className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Contact Tab */}
              <TabsContent value="contact" className="space-y-6 mt-0">
                <div className="space-y-5">
                  <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b border-gray-200">
                    Contact Information
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Contact Name *
                    </label>
                    <Input
                      value={formData.contact.primaryName}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          contact: { ...formData.contact, primaryName: e.target.value },
                        })
                      }
                      placeholder="Jane Smith"
                    />
                    {validationErrors.primaryName && (
                      <p className="text-xs text-red-600 mt-1">{validationErrors.primaryName}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Contact Email *
                    </label>
                    <Input
                      type="email"
                      value={formData.contact.primaryEmail}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          contact: { ...formData.contact, primaryEmail: e.target.value },
                        })
                      }
                      placeholder="jane@example.com"
                    />
                    {validationErrors.primaryEmail && (
                      <p className="text-xs text-red-600 mt-1">{validationErrors.primaryEmail}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Contact Phone
                    </label>
                    <Input
                      type="tel"
                      value={formData.contact.primaryPhone}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          contact: { ...formData.contact, primaryPhone: e.target.value },
                        })
                      }
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Billing Email
                    </label>
                    <Input
                      type="email"
                      value={formData.contact.billingEmail}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          contact: { ...formData.contact, billingEmail: e.target.value },
                        })
                      }
                      placeholder="billing@example.com"
                    />
                    {validationErrors.billingEmail && (
                      <p className="text-xs text-red-600 mt-1">{validationErrors.billingEmail}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Technical Email
                    </label>
                    <Input
                      type="email"
                      value={formData.contact.technicalEmail}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          contact: { ...formData.contact, technicalEmail: e.target.value },
                        })
                      }
                      placeholder="tech@example.com"
                    />
                    {validationErrors.technicalEmail && (
                      <p className="text-xs text-red-600 mt-1">
                        {validationErrors.technicalEmail}
                      </p>
                    )}
                  </div>
                </div>
              </TabsContent>
            </div>
            {/* End of Scrollable Content Area */}
          </Tabs>

          {/* Footer */}
          <div className="border-t px-6 py-4">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Saving...' : mode === 'create' ? 'Create Enterprise' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EnterpriseModal;
